import React, { useEffect, useState } from 'react';

import { Select } from 'antd';
import { isMobileOnly } from 'react-device-detect';
import { CreativeType } from 'types/creative';
import { useAppState, useAppDispatch } from 'context';
import { setSizeChangerList, setInitialData, setCreativeViewSize, setLoading, setError, setParams } from 'context/actions';
import { request } from 'services/server';
import { classSize } from 'services/constants';
import { buildCreativeSizes } from 'services/utils';
import config from 'config';
import KervLogo from 'components/atoms/KervLogo/KervLogo';

import './GalleryHeader.scss';

type CreativeOptions = {
  id: number,
  name: string,
};

interface GalleryHeaderProps {
  options: CreativeOptions[],
  order: number[],
}

const { Option } = Select;

const incomingOptions = (data: CreativeOptions[] = []) => data.map((option, idx) => <Option key={`${option.id}`} value={option.id}>{option.id} - {option.name}</Option>);

const GalleryHeader = ({ options, order }: GalleryHeaderProps) => {
  const dispatch = useAppDispatch();
  const { creative = {} as CreativeType, screenSizeClass, galleryParams = {}, playerSizeFormat} = useAppState();
  const { id } = creative;
  const { name } = creative;
  const [activeCreative, setActiveCreative] = useState<number | undefined>(undefined)
  // sort to match order prop
  options.sort((a, b) => order.indexOf(a.id) - order.indexOf(b.id));
  
  // on mount set active creative based on first index of incoming options
  useEffect(() => {
    if(options.length) {
      setActiveCreative((options[0] || {}).id)
    }
  }, [options]);

  useEffect(() => {
    (async () => {
      if (activeCreative) {
        dispatch(setLoading(true));
        const { success: response, error } = await request(`${config.api.root}/creatives/${activeCreative}/player?cb=${Date.now().toString()}`);
        dispatch(setLoading(false));
        if (response) {
          dispatch(setInitialData(response.data));
          const creativeParams = galleryParams[activeCreative]
          dispatch(setParams(creativeParams));
        } else {
          const { errors, status } = error;
          const message = errors ? errors[0].message : error;
          dispatch(setError(message));
          // TODO: how do we handle user messaging and UX if no creative returned or no options in parent component?
        }
      }
    })();
  }, [activeCreative]);

  useEffect(() => {
    if(id) {
      if(!isMobileOnly) { // mobile view won't need a dropdown
        // console.debug(creative)
        const dropdown =  buildCreativeSizes(creative)
        dispatch(setSizeChangerList(dropdown)); // save size options
        dispatch(setCreativeViewSize({...dropdown[parseInt(playerSizeFormat, 10)].size, share: true})) // default size
      } else { // most mobile views will be 100% except for banners, so modify in lower components
        dispatch(setCreativeViewSize({width: '100%', height: '100%', share: true}))
      } 
    }
  }, [id, playerSizeFormat]);

  // TODO: determine if need sidebar solution and apply
  let vertical = false;

  const handleSelect = (val: any) => setActiveCreative(val);

  return (
    <div className={`header ${screenSizeClass}`}>
      {screenSizeClass === classSize.sm && <KervLogo />}
      {screenSizeClass !== classSize.sm && <h1>Preview Gallery</h1>}
      {!!options.length &&
        <div className={`gallery-header-bar ${vertical ? 'vertical' : 'horizontal'}`}>
          <div className="creative-name">{name}</div>
          <div className="options-array">
          Creative:
            <Select
              placeholder='Select Creative'
              onSelect={handleSelect}
              defaultOpen={false}
              popupClassName={`crv-select ${isMobileOnly ? 'mobile' : ''}`}
              placement='bottomRight'
              dropdownMatchSelectWidth={false}
              defaultValue={`${options[0].id} - ${options[0].name}`}
            >
              {incomingOptions(options.filter(option => option.id !== id))}
            </Select>
          </div>
        </div>
      }
    </div>
  );
}

export default GalleryHeader;
